import { PlaceholderKind } from '@woundtech/ui-constants';
import { fullAddressFormat } from 'fhir/general/Address';
import { addressCountyAndMarket } from 'fhir/general/Address/formatters';
import { phoneNumber } from 'styleguide/form/Telecom/formatters';
import { INPUT_MASK_PHONE_NUMBER, textMaskFormat } from 'styleguide/form/Text';
import {
  CONTACT_PERSON_STATUS,
  HOME_HEALTH_CODE,
  INSTRUCTION_TYPE,
  MOBILE_CODE,
  NPI_SYSTEM,
  ORGNAIZATION_SUB_TYPE,
  ORGNAIZATION_TYPE,
  WORK_CODE,
} from '../../constants';
import { getSystemCode, getTelecomValue, groupBy } from '../utils';
import { identifierBySystem } from 'fhir/general/Identifier/formatters';

export const getOrganizationDataAttributes = (organization, isHomeHealthV2FlagEnabled = false) => {
  const careName = organization?.name;
  const careType = getSystemCode(organization?.type, ORGNAIZATION_SUB_TYPE)
    ?.coding?.[0]?.display;
  const isHomeHealth =
    getSystemCode(organization?.type, ORGNAIZATION_TYPE)?.coding?.[0]?.code ===
    HOME_HEALTH_CODE;
  const careStatus = organization?.active ? 'Active' : 'Inactive';

  const npiNumber = identifierBySystem(
    organization?.identifier,
    NPI_SYSTEM
  )

  const npiData = isHomeHealthV2FlagEnabled ? [{
    name: 'NPI',
    value: npiNumber,
    columns: 2,
  }] : []

  return [
    {
      name: 'Facility Name',
      value: careName || PlaceholderKind.NotAvailable,
      columns: 2,
    },
    ...npiData,
    ...(!isHomeHealth
      ? [
          {
            name: 'Type',
            value: careType || PlaceholderKind.NotAvailable,
            columns: 2,
          },
        ]
      : []),
    {
      name: 'Status',
      value: careStatus || PlaceholderKind.NotAvailable,
      columns: 2,
    },
  ];
};

export const getAddressAttributes = (
  organization,
  showInstruction,
  showMarket
) => {
  const address = organization?.address?.[0];
  const instruction = address?.extension?.find(
    (item) => item?.url === INSTRUCTION_TYPE
  )?.valueString;
  return [
    {
      name: 'Address',
      value: fullAddressFormat(address) || PlaceholderKind.NotAvailable,
      columns: 2,
    },
    ...(showMarket
      ? [
          {
            name: 'Market',
            value: addressCountyAndMarket(address),
            columns: 2,
          },
        ]
      : []),
    ...(showInstruction
      ? [
          {
            name: 'Instructions',
            value: instruction || PlaceholderKind.NotAvailable,
            columns: 2,
          },
        ]
      : []),
  ];
};

export const getOrganizationTelecom = (
  organization,
  telecomData = undefined
) => {
  const telecom = telecomData || organization?.telecom;
  const telecomGrouped = groupBy(telecom, 'rank');
  return telecomGrouped?.map((item) => {
    return [
      {
        name: 'Phone Number',
        value:
          phoneNumber(
            item?.telecom?.find((item) => item?.system === 'phone')
          ) || PlaceholderKind.NotAvailable,
        columns: 2,
      },
      {
        name: 'Fax',
        value:
          textMaskFormat(
            getTelecomValue(item?.telecom, 'fax'),
            INPUT_MASK_PHONE_NUMBER
          ) || PlaceholderKind.NotAvailable,
      },
      {
        name: 'Email',
        value:
          getTelecomValue(item?.telecom, 'email') ||
          PlaceholderKind.NotAvailable,
      },
    ];
  });
};

export const getContactPersonAttributes = (
  contact,
  telecomData = undefined
) => {
  const telecom = telecomData || contact?.telecom;
  const phoneDetails = telecom?.filter((item) => item.system === 'phone');
  const mobilePhone = phoneNumber(
    phoneDetails?.find((item) => item?.use === MOBILE_CODE)
  );
  const workPhone = phoneNumber(
    phoneDetails?.find((item) => item?.use === WORK_CODE)
  );

  return [
    {
      icon: 'phone',
      value: workPhone || PlaceholderKind.NotAvailable,
    },
    {
      icon: 'phone',
      value: mobilePhone || PlaceholderKind.NotAvailable,
    },
    {
      name: 'Fax',
      value:
        textMaskFormat(
          getTelecomValue(telecom, 'fax'),
          INPUT_MASK_PHONE_NUMBER
        ) || PlaceholderKind.NotAvailable,
    },
    {
      name: 'Email',
      value: getTelecomValue(telecom, 'email') || PlaceholderKind.NotAvailable,
    },
  ];
};

export const getContactPersonStatus = (contact) => {
  return contact?.extension?.find((item) => item?.url === CONTACT_PERSON_STATUS)
    ?.valueBoolean;
};
